/**
 * Real candlestick price chart, built on TradingView's open-source
 * lightweight-charts library — replacing ad hoc SVG for anything showing
 * genuine OHLC price history.
 */

import React, { useEffect, useRef } from "react";
import { ColorType, createChart, IChartApi, ISeriesApi } from "lightweight-charts";

export interface CandlestickPoint {
  time: string; // ISO date, e.g. "2026-01-15"
  open: number;
  high: number;
  low: number;
  close: number;
}

interface CandlestickChartProps {
  data: CandlestickPoint[];
  height?: number;
  targetFps?: number;
}

export default function CandlestickChart({
  data,
  height = 320,
  targetFps = 60,
}: CandlestickChartProps): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const chart = createChart(containerRef.current, {
      height,
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor: "#8B95AC",
        fontFamily: "'IBM Plex Mono', monospace",
      },
      grid: {
        vertLines: { color: "rgba(255,255,255,0.04)" },
        horzLines: { color: "rgba(255,255,255,0.04)" },
      },
      rightPriceScale: { borderColor: "#1B2540" },
      timeScale: { borderColor: "#1B2540" },
      // On low-performance hardware, disabling interactive pan/zoom avoids
      // continuous re-render churn during drag — a real cost in this
      // library, unlike crosshair styling which doesn't meaningfully affect it.
      handleScroll: targetFps > 15,
      handleScale: targetFps > 15,
    });

    const series = chart.addCandlestickSeries({
      upColor: "#2FD3A6",
      downColor: "#EF6E6E",
      borderVisible: false,
      wickUpColor: "#2FD3A6",
      wickDownColor: "#EF6E6E",
    });

    chartRef.current = chart;
    seriesRef.current = series;

    const resizeObserver = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      chart.applyOptions({ width });
    });
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height, targetFps]);

  useEffect(() => {
    if (!seriesRef.current) return;
    const formatted = data.map((point) => ({
      time: point.time,
      open: point.open,
      high: point.high,
      low: point.low,
      close: point.close,
    }));
    seriesRef.current.setData(formatted as never);
    chartRef.current?.timeScale().fitContent();
  }, [data]);

  if (data.length === 0) {
    return (
      <div
        style={{ height }}
        className="flex items-center justify-center text-sm text-mist-400"
      >
        No price history available yet.
      </div>
    );
  }

  return <div ref={containerRef} className="w-full" />;
}
